import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import WaterMark from "../WaterMark/WaterMark";
import { Loader } from "rsuite";
import { TbHandClick } from "react-icons/tb";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { axInst } from "../Login/Service";

const region_table_details = async (body, endpoint, QueryKey) => {
  try {
    const res = await axInst.post(endpoint + "region_" + QueryKey, body);
    const obj = res.data;

    const beatPlan_sum = Object.values(obj).reduce(
      (sum, e) => sum + e.BeatPlan,
      0
    );
    const btl_sum = Object.values(obj).reduce((sum, e) => sum + e.BTL, 0);
    const digital_sum = Object.values(obj).reduce(
      (sum, e) => sum + e.Digital,
      0
    );
    const tgm_sum = Object.values(obj).reduce((sum, e) => sum + e.TGM, 0);
    const atl_sum = Object.values(obj).reduce((sum, e) => sum + e.ATL, 0);
    const total_sum = Object.values(obj).reduce((sum, e) => sum + e.Total, 0);

    const formattedData = Object.keys(obj).map((region) => ({
      Region: region,
      BeatPlan: obj[region].BeatPlan,
      BTL: obj[region].BTL,
      Digital: obj[region].Digital,
      TGM: obj[region].TGM,
      ATL: obj[region].ATL,
      Total: obj[region].Total,
      States: [],
    }));

    return {
      obj: formattedData,
      beatPlan_sum,
      btl_sum,
      atl_sum,
      digital_sum,
      tgm_sum,
      total_sum,
    };
  } catch (error) {
    console.error(error);
    return {
      obj: [],
      beatPlan_sum: 0,
      btl_sum: 0,
      digital_sum: 0,
      tgm_sum: 0,
      total_sum: 0,
    };
  }
};

const state_table_details = async (requestBody, endpoint, QueryKey) => {
  try {
    const res = await axInst.post(endpoint + "state_" + QueryKey, requestBody);
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

const dealerwise_table_details = async (requestBody, endpoint, QueryKey) => {
  try {
    const res = await axInst.post(endpoint + "dealer_" + QueryKey, requestBody);
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

const TwoDrilldownTable = ({
  heading,
  columns,
  endpoint,
  QueryKey,
  className,
}) => {
  const { data: filterdata, submitted } = useSelector(
    (state) => state.filtersLeadSource
  );
  const { data, isFetching, isError } = useQuery({
    queryKey: [QueryKey, submitted],
    queryFn: () => region_table_details(filterdata, endpoint, QueryKey),
    initialData: {
      obj: [],
      beatPlan_sum: 0,
      btl_sum: 0,
      digital_sum: 0,
      tgm_sum: 0,
      total_sum: 0,
    },
    // refetchInterval: 300000,
    // refetchIntervalInBackground: true,
    cacheTime: 0,
    staleTime: 0,
  });

  const [sorting, setSorting] = useState([]);
  const [filtering, setFiltering] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    return () => {
      setExpandedRows({});
    };
  }, []);

  const toggleRowExpanded = async (region, state = null, filterdata) => {
    try {
      if (state) {
        const requestBody = {
          ...filterdata, // preserve user-applied filters
          region: [region], // override region
          state: [state], // override state
        };
        if (!expandedRows[region][state]?.isExpanded) {
          const dealerData = await dealerwise_table_details(
            requestBody,
            endpoint,
            QueryKey
          );

          setExpandedRows((prev) => ({
            ...prev,
            [region]: {
              ...prev[region],
              [state]: {
                ...prev[region][state],
                isExpanded: true,
                dealerData: dealerData,
              },
            },
          }));
        } else {
          setExpandedRows((prev) => ({
            ...prev,
            [region]: {
              ...prev[region],
              [state]: {
                ...prev[region][state],
                isExpanded: false,
              },
            },
          }));
        }
      } else {
        const requestBody = {
          ...filterdata, // preserve user-applied filters
          region: [region], // override region
          state: [], // reset state
        };
        if (!expandedRows[region]) {
          const stateData = await state_table_details(
            requestBody,
            endpoint,
            QueryKey
          );
          const stateExpansion = Object.keys(stateData).reduce(
            (acc, key) => ({
              ...acc,
              [key]: {
                isExpanded: false,
                data: stateData[key] || {},
              },
            }),
            {}
          );

          setExpandedRows((prev) => ({
            ...prev,
            [region]: stateExpansion,
          }));
        } else {
          setExpandedRows((prev) => ({
            ...prev,
            [region]: false,
          }));
        }
      }
    } catch (error) {
      console.error("Error toggling row expansion:", error);
    }
  };

  const table = useReactTable({
    data: data?.obj ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: sorting,
      globalFilter: filtering,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
  });

  return (
    <div className={`relative bg-white rounded-md ${className}`}>
      <header className="pb-1 text-base font-semibold text-[#4c4c4c]">
        {heading}
      </header>
      <div className="self-center overflow-auto max-h-[15rem] ">
        {isError && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Error Occurred</p>
          </div>
        )}
        {isFetching && (
          <div className="grid items-center justify-center w-full h-full">
            <Loader />
          </div>
        )}
        {!isFetching && !isError && !data && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Data Not Available</p>
          </div>
        )}
        {!isFetching && !isError && data && (
          <>
            <WaterMark />
            <table className="w-full border-collapse">
              <thead className="sticky text-white top-0 bg-[#2A5775] text-left z-[1]">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className={`p-[1px] whitespace-nowrap text-sm border text-center border-gray-300 bg-[#2A5775] ${
                          index === 0
                            ? "w-[80px] sticky left-0 top-0 z-20 border-r border-b "
                            : "z-auto border-b"
                        }`}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`even:bg-gray-200 odd:bg-white ${
                        rowIndex === 0 ? "cursor-default" : "cursor-pointer"
                      } ${
                        expandedRows[row.original.Region] ? "font-bold" : ""
                      }`}
                      onClick={() =>
                        row.original.Region !== "All Regions" &&
                        toggleRowExpanded(row.original.Region, null, filterdata)
                      }
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <td
                          key={cell.id}
                          className={`p-[1px] text-xs text-center whitespace-nowrap  lg:text-sm border border-gray-300 h-[30px]    ${
                            cellIndex === 0
                              ? "sticky left-0  bg-[#d4e1ec]  border-r "
                              : "z-auto"
                          }`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                          {cellIndex === 0 &&
                            row.original.Region !== "All Regions" && (
                              <div className="absolute right-0 top-2">
                                {expandedRows[row.original.Region] ? (
                                  <IoMdArrowDropup size={14} />
                                ) : (
                                  <IoMdArrowDropdown size={14} />
                                )}
                              </div>
                            )}
                        </td>
                      ))}
                    </tr>
                    {expandedRows[row.original.Region] &&
                      Object.entries(expandedRows[row.original.Region]).map(
                        ([state, stateDetails]) => (
                          <React.Fragment key={state}>
                            <tr
                              className={`bg-[#2A5775] text-white ${
                                stateDetails.isExpanded
                                  ? "font-bold"
                                  : "font-normal"
                              }`}
                              onClick={() =>
                                toggleRowExpanded(
                                  row.original.Region,
                                  state,
                                  filterdata
                                )
                              }
                            >
                              <td className="p-1 text-sm whitespace-nowrap text-center lg:text-sm border border-gray-300 h-8  bg-[#2A5775] sticky left-0 ">
                                <span className="ml-2 mr-4 ">{state}</span>
                                <div className="absolute right-0 top-2">
                                  {stateDetails.isExpanded ? (
                                    <IoMdArrowDropup size={14} />
                                  ) : (
                                    <IoMdArrowDropdown size={14} />
                                  )}
                                </div>
                              </td>
                              <td className="p-1 text-xs text-center  lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.BeatPlan}
                              </td>
                              <td className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.BTL}
                              </td>
                              <td className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.Digital}
                              </td>
                              <td className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.ATL}
                              </td>
                              <td className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.TGM}
                              </td>
                              <td className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.Total}
                              </td>
                            </tr>
                            {stateDetails.isExpanded &&
                              Object.entries(stateDetails.dealerData).map(
                                ([dealer, dealerDetails]) => (
                                  <tr
                                    key={dealer}
                                    className="even:bg-gray-50 odd:bg-white text-[12px]"
                                  >
                                    <td className="p-1 text-sm text-left  lg:text-sm border border-gray-300 h-8 sticky left-0  bg-white">
                                      <span className="ml-2 text-[12px] whitespace-nowrap">
                                        {dealer}
                                      </span>
                                    </td>
                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8 ">
                                      {dealerDetails.BeatPlan}
                                    </td>
                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8  ">
                                      {dealerDetails.BTL}
                                    </td>
                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8 ">
                                      {dealerDetails.Digital}
                                    </td>
                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8 ">
                                      {dealerDetails.ATL}
                                    </td>
                                    <td className="p-1  text-xs text-center lg:text-sm border border-gray-300 h-8 ">
                                      {dealerDetails.TGM}
                                    </td>
                                    <td className="p-1 text-xs text-center lg:text-sm border border-gray-300 h-8 ">
                                      {dealerDetails.Total}
                                    </td>
                                  </tr>
                                )
                              )}
                          </React.Fragment>
                        )
                      )}
                  </React.Fragment>
                ))}
                <tr className="bg-[#1C5982]  text-center text-white text-xs lg:text-sm">
                  <td className="p-[2px]   lg:text-base border border-gray-300 sticky left-0 z-10 bg-[#1C5982]">
                    Total
                  </td>
                  <td className="p-[2px]   lg:text-base border border-gray-300 ">
                    {data?.beatPlan_sum ?? 0}
                  </td>
                  <td className="p-[2px]  lg:text-base border border-gray-300 ">
                    {data?.btl_sum ?? 0}
                  </td>
                  <td className="p-[2px]  lg:text-base border border-gray-300 ">
                    {data?.digital_sum ?? 0}
                  </td>
                  <td className="p-[2px]  lg:text-base border border-gray-300 ">
                    {data?.atl_sum ?? 0}
                  </td>
                  <td className="p-[2px]  lg:text-base border border-gray-300 ">
                    {data?.tgm_sum ?? 0}
                  </td>
                  <td className="p-[2px]  lg:text-base border border-gray-300 ">
                    {data?.total_sum ?? 0}
                  </td>
                </tr>
              </tbody>
            </table>

            {QueryKey === "pipeline_lead" ? (
              <span className="text-xs">
                For Pipeline, only MTD C0, MTD C1, MTD C1A, and MTD C2 are
                considered.
              </span>
            ) : (
              <p className="text-xs mt-1 flex items-center">
                <TbHandClick /> on Region to view States, Dealer Wise
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TwoDrilldownTable;
